import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import './GeneratedRecipePreview.css';
import { HerbSuggestion } from '../types';
import { GENERATED_RECIPE_PREVIEW_KEY } from '../constants/storageKeys';

interface GeneratedRecipePreviewData {
  recipeTitle: string;
  recipeContent: string;
  herbs: HerbSuggestion[];
  diagnosis?: string | null;
  sourcePrescriptionTitle?: string | null;
  sourcePrescriptionId?: string | null;
  createdAt?: string;
}

const GeneratedRecipePreview: React.FC = () => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<GeneratedRecipePreviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    try {
      const raw = window.sessionStorage.getItem(GENERATED_RECIPE_PREVIEW_KEY);
      if (!raw) {
        setPreview(null);
      } else {
        const parsed = JSON.parse(raw) as GeneratedRecipePreviewData;
        if (parsed?.recipeContent) {
          setPreview(parsed);
        } else {
          setPreview(null);
        }
      }
    } catch (err) {
      console.error('读取药膳预览数据失败', err);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBackToHerbLibrary = () => {
  navigate('/app/herb-library');
  };

  const handleViewPrescription = () => {
    if (preview?.sourcePrescriptionId) {
  navigate(`/app/prescriptions/${preview.sourcePrescriptionId}`);
    }
  };

  if (loading) {
    return (
      <div className="unified-page-wrapper">
        <div className="unified-page-header">
          <h3>药膳详情加载中...</h3>
        </div>
        <div className="unified-page-content-scrollable">
          <div className="generated-recipe-loading">正在载入，请稍候...</div>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <motion.div
        className="unified-page-wrapper generated-recipe-page"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="unified-page-header generated-recipe-header">
          <div>
            <span className="generated-recipe-breadcrumb">AI 药膳转换</span>
            <h2>暂无药膳详情</h2>
            <p className="generated-recipe-note">请在中药材库中选择药材并点击“转为药膳”以生成新的药膳方案。</p>
          </div>
        </div>
        <div className="unified-page-content-scrollable">
          <div className="generated-recipe-empty premium-card">
            <p>当前没有可展示的药膳方案。</p>
            <button type="button" className="outline-btn" onClick={handleBackToHerbLibrary}>
              返回药材库
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  const generatedAt = preview.createdAt ? new Date(preview.createdAt) : null;

  return (
    <motion.div
      className="unified-page-wrapper generated-recipe-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="unified-page-header generated-recipe-header">
        <div>
          <span className="generated-recipe-breadcrumb">AI 药膳转换</span>
          <h2>{preview.recipeTitle || 'AI 药膳方案'}</h2>
          {generatedAt && (
            <p className="generated-recipe-meta">生成时间：{generatedAt.toLocaleString()}</p>
          )}
          {preview.sourcePrescriptionTitle && (
            <p className="generated-recipe-meta">来源药方：{preview.sourcePrescriptionTitle}</p>
          )}
          {preview.diagnosis && (
            <p className="generated-recipe-meta">辨证要点：{preview.diagnosis}</p>
          )}
        </div>
        <div className="generated-recipe-header-actions">
          <button type="button" className="outline-btn" onClick={handleBackToHerbLibrary}>
            返回药材库
          </button>
          {preview.sourcePrescriptionId && (
            <button type="button" className="outline-btn" onClick={handleViewPrescription}>
              查看药方详情
            </button>
          )}
        </div>
      </div>

      <div className="unified-page-content-scrollable generated-recipe-content">
        {preview.herbs?.length > 0 && (
          <section className="premium-card generated-recipe-herbs">
            <h4>原方药材搭配</h4>
            <ul className="generated-recipe-herb-list">
              {preview.herbs.map((herb) => (
                <li key={herb.name}>
                  <strong>{herb.name}</strong>
                  {herb.dosage && <span className="herb-dose"> · {herb.dosage}</span>}
                  {herb.reason && <span className="herb-reason"> — {herb.reason}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="premium-card generated-recipe-markdown">
          <h4>药膳制作指引</h4>
          <ReactMarkdown rehypePlugins={[rehypeSanitize]} remarkPlugins={[remarkGfm]}>
            {preview.recipeContent}
          </ReactMarkdown>
        </section>
      </div>
    </motion.div>
  );
};

export default GeneratedRecipePreview;
